import React, { useMemo, useState } from 'react';
import { ArrowRight, CheckCircle, ShoppingCart, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import { productCategories, products as fallbackProducts } from '../data/siteData';
import { getContentIcon, useCollectionData } from '../services/contentApi';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import ProductDetailModal from '../components/ProductDetailModal';
import './Products.css';

const catImageMap = {
  Bedding:        '/assets/products/bedding.png',
  Towels:         '/assets/products/towels.png',
  'Patient Wear': '/assets/products/patient-wear.png',
  Specialty:      '/assets/products/specialty.png',
};

const Products = ({ onRequireAuth }) => {
  const [active, setActive]           = useState('All');
  const [selectedProduct, setSelected] = useState(null);
  const { items: dbProducts }         = useCollectionData('products', fallbackProducts);
  const { isLoggedIn, isProfileComplete } = useAuth();
  const { addItem }                   = useCart();

  // Merge DB products with fallback (DB products take precedence, fallback fills gaps)
  const products = useMemo(() => {
    if (dbProducts && dbProducts.length > 0) {
      return dbProducts.map((p, i) => ({
        ...fallbackProducts[i],
        ...p,
        image: p.image || (fallbackProducts[i]?.image) || catImageMap[p.cat],
        price: p.price || fallbackProducts[i]?.price,
      }));
    }
    return fallbackProducts;
  }, [dbProducts]);

  const filtered = useMemo(
    () => (active === 'All' ? products : products.filter((p) => p.cat === active)),
    [active, products]
  );

  const handleAddToCart = (product, e) => {
    e.stopPropagation();
    if (!isLoggedIn || !isProfileComplete) {
      if (onRequireAuth) onRequireAuth();
      return;
    }
    addItem({
      ...product,
      image: product.image || catImageMap[product.cat],
    });
  };

  return (
    <>
      <section className="page-hero products-hero">
        <div className="container page-hero-grid">
          <div>
            <p className="eyebrow">Product catalog</p>
            <h1 className="page-title">Infection-control-grade healthcare linens.</h1>
          </div>
          <p className="page-kicker">
            Woven and non-woven textiles engineered for durability, patient
            comfort, and compliance with CDC, OSHA, HLAC, and FDA textile
            expectations.
          </p>
        </div>
      </section>

      <section className="section products-page-section">
        <div className="container">
          <div className="product-filters" aria-label="Filter products by category">
            {productCategories.map((category) => (
              <button
                type="button"
                className={`filter-btn ${active === category ? 'active' : ''}`}
                onClick={() => setActive(category)}
                key={category}
              >
                {category}
              </button>
            ))}
          </div>

          <div className="products-grid">
            {filtered.map((product, index) => {
              const { name, cat, material, spec, sizes, feature, badge, tone, icon, price, image } = product;
              const Icon   = getContentIcon(icon);
              const imgSrc = image || catImageMap[cat] || '/assets/products/bedding.png';

              return (
                <article
                  className="catalog-card stagger-card"
                  key={name || index}
                  style={{ '--delay': `${index * 55}ms` }}
                >
                  {/* Product Image */}
                  <div className="catalog-img-wrap">
                    <img src={imgSrc} alt={name} loading="lazy" />
                    {badge && <span className="catalog-badge">{badge}</span>}
                    <button
                      className="catalog-quick-view"
                      onClick={() => setSelected(product)}
                      aria-label={`View details for ${name}`}
                    >
                      <Eye size={14} /> Quick view
                    </button>
                  </div>

                  {/* Card Body */}
                  <div className="catalog-body">
                    <div className="catalog-top">
                      <span className={`icon-circle tone-${tone || 'blue'}`}>
                        <Icon size={18} strokeWidth={1.8} aria-hidden="true" />
                      </span>
                      <span className="catalog-category">{cat}</span>
                    </div>

                    <h2 className="catalog-name">{name}</h2>

                    <div className="catalog-meta">
                      <span>{material}</span>
                      <span>{spec}</span>
                    </div>

                    <div className="catalog-feature">
                      <CheckCircle size={14} aria-hidden="true" />
                      {feature}
                    </div>

                    <p className="catalog-sizes">Sizes: {sizes}</p>

                    {/* Price */}
                    {price && (
                      <div className="catalog-price">
                        <span className="catalog-price-from">from</span>
                        <span className="catalog-price-val">{price}</span>
                        <span className="catalog-price-unit">/ unit</span>
                      </div>
                    )}

                    <div className="catalog-actions">
                      <button
                        className="btn-cart"
                        onClick={(e) => handleAddToCart(product, e)}
                        aria-label={`Add ${name} to cart`}
                      >
                        <ShoppingCart size={14} />
                        {isLoggedIn && isProfileComplete ? 'Add to Cart' : 'Sign in to Order'}
                      </button>
                      <button
                        className="btn-details"
                        onClick={() => setSelected(product)}
                        aria-label={`View details for ${name}`}
                      >
                        Details <ArrowRight size={14} aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          <div className="catalog-cta surface-card">
            <div>
              <p className="eyebrow">Bulk ordering</p>
              <h2>Need a full SKU list or custom order?</h2>
              <p>Send monthly usage, storage limits, and preferred delivery rhythm. We will shape a catalog around the facility.</p>
            </div>
            <Link className="btn-primary" to="/contact">
              Get full catalog <ArrowRight size={18} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* Product Detail Modal */}
      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={() => setSelected(null)}
          onRequireAuth={() => { setSelected(null); if (onRequireAuth) onRequireAuth(); }}
        />
      )}
    </>
  );
};

export default Products;
